import React, { useState, useMemo, useCallback, useContext } from 'react';
import { some } from 'lodash';
import { FormInstance } from 'antd/lib/form';
import { PlusOutlined } from '@ant-design/icons';
import {
  Form,
  Input,
  notification,
  Popconfirm,

  Row,
  Col,
  Button as AntButton,
} from 'antd';

import { useFormList } from 'hooks/useFormList';
import Value from 'components/Value';
import DataGrid from 'components/DataGrid/DataGrid';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import ParListByParcodeSelect from '../../Components/ParListByParcodeSelect';
import { CloseOutlined } from '@ant-design/icons';
import {
  createParList,
  removeParList,
  updateParList,
} from '../../../../services/parametersService';
import DeleteButton from 'components/DeleteButton';
import SaveButton from 'components/SaveButtonAction';
import { NormalizeAlphAndNum, setValueByNamePath } from 'utils/utils2';
import { AuthContext } from 'components/Auth/Auth';
import Button from 'components/Button';

import SaveButtonIcon from 'components/SaveBtn';
import _ from 'lodash';

type MenuItemsTableProps = {
  form: FormInstance;
  ParcodeParent?: string;
  refetch: () => void;
  flagworkflow?: boolean;
};

type SearchParams = {
  parvalue: string;
  parlistlabel: string;
  parvalueparent: string;
};

const ParameterValuesTable = (props: MenuItemsTableProps) => {
  const userrole = localStorage.getItem('userrole');
  const { form, ParcodeParent, refetch, flagworkflow } = props;
  // eslint-disable-next-line no-unused-vars
  const [searchs, setsearchs] = useState(false);
  const { isInspect } = useContext(AuthContext);
  const fieldName = 'parLists';

  const { add, remove } = useFormList(form, fieldName, {
    idKey: 'parvalue',
    addAsFirst: true,
  });

  const data = form.getFieldValue(fieldName).map((value: any) => ({
    ...value,
    id:Math.round(_.random(10000000)),
  }));

  const initialSearchParams: SearchParams = {
    parvalue: '',
    parlistlabel: '',
    parvalueparent: '',
  };

  const [searchParams, setSearchParams] =
    useState<SearchParams>(initialSearchParams);

  const indexById = data.reduce(
    (prevValue: any, row: any, index: number) => ({
      ...prevValue,
      [row.id]: index,
    }),
    {}
  );

  const getName = useCallback(
    (name: string) => {
      return (record: any, index: number) => [
        fieldName,
        indexById[record.id],
        name,
      ];
    },
    [indexById]
  );

  const onSaveOrEdit = useCallback(
    (record: any) => {
      const parcode = form.getFieldValue('parcode');
      const currentValue = form.getFieldValue(fieldName)[indexById[record.id]];
      const body = {
        ...currentValue,
        parcode,
        // parliststatus: flagworkflow === true ? 'INIT' : 'ACTIVE',
      };
      if (currentValue.parvalue !== '' && currentValue.parvalue.length <= 10) {
        currentValue?.isNew
          ? createParList({ parListRequest: body }).then((response) => {
              if (response.code === '200') {
                notification.success({
                  message: `${currentValue.parvalue} is created`,
                });
                refetch();
              } else {
                notification.error({
                  message: response.message,
                  description: response.description,
                });
              }
            })
          : updateParList({
              parListRequest: body,
            }).then(() => {
              notification.success({
                message: `${currentValue.parvalue} is updated`,
              });
            });
      } else {
        notification.error({
          message: 'Field value is required and should be less than 10 chars',
        });
      }
    },
    [form, indexById, searchParams, refetch]
  );

  const columns = useMemo<DataGridColDef<any>[]>(
    () => [
      {
        dataIndex: 'parvalue',
        render: (text, record) => {
          return record?.isNew ? (
            <Form.Item
              noStyle
              name={getName('parvalue')(record, indexById[record.id])}
              normalize={NormalizeAlphAndNum}
              rules={[
                {
                  required: true,
                  message: 'Max 10 characters',
                  max: 10,
                },
              ]}
            >
              <Input />
            </Form.Item>
          ) : (
            <Form.Item>
              <Value value={text}></Value>
            </Form.Item>
          );
        },
        width: 150,
        filtrable: true,
        fixed: 'left',
      },
      {
        dataIndex: 'parlistlabel',
        render: (value, record) => {
          return (
            <Form.Item
              noStyle
              name={getName('parlistlabel')(record, indexById[record.id])}
            >
              <Input />
            </Form.Item>
          );
        },
        width: 400,
        filtrable: true,
      },
      {
        dataIndex: 'parlistdescription',
        render: (value, record) => {
          return (
            <Form.Item
              noStyle
              name={getName('parlistdescription')(record, indexById[record.id])}
            >
              <Input />
            </Form.Item>
          );
        },
        width: 350,
      },
      {
        dataIndex: 'parvalueparent',
        filtrable: true,
        filterComponent: (
          <ParListByParcodeSelect
            showSearch
            allowClear
            filterOption={(input, option) =>
              option?.props.children
                .toLowerCase()
                .indexOf(input.toLowerCase()) >= 0 ||
              option?.props.value.toLowerCase().indexOf(input.toLowerCase()) >=
                0
            }
            parcode={ParcodeParent ?? ''}
          />
        ),
        render: (value, record) => {
          return (
            <Form.Item
              noStyle
              name={getName('parvalueparent')(record, indexById[record.id])}
            >
              {ParcodeParent?.length !== 0 && (
                <ParListByParcodeSelect
                  showSearch
                  allowClear
                  filterOption={(input, option) =>
                    option?.props.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0 ||
                    option?.props.value
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  parcode={ParcodeParent ?? ''}
                />
              )}
            </Form.Item>
          );
        },
        width: 250,
      },
      {
        dataIndex: 'parvalueexternal',
        render: (value, record) => {
          return (
            <Form.Item
              noStyle
              name={getName('parvalueexternal')(record, indexById[record.id])}
            >
              <Input />
            </Form.Item>
          );
        },
 
        width: 250,
      },
      {
        dataIndex: 'parliststatus',
        // render: (value, record) => {
        //   return (
        //     <Form.Item
        //       noStyle
        //       name={getName('parliststatus')(record, indexById[record.id])}
        //     >
        //       {flagworkflow === true ? 'INIT' : 'ACTIVE'}
        //     </Form.Item>
        //   );
        // },
        width: 250,
        fixed: 'right',
      },
      {
        dataIndex: 'Actions',
        render: (value, record, index) =>
          record?.isNew ? (
            <Row>
              <Col>
                <Form.Item noStyle shouldUpdate={() => true}>
                  {({ getFieldValue }) => (
                    <SaveButton
                      comcode="save"
                      size="middle"
                      onClick={() => onSaveOrEdit(record)}
                      htmlType="button"
                      disabled={
                        form.getFieldValue([fieldName, index, 'parvalue'])
                          .length === 0 ||
                        form.getFieldValue([fieldName, index, 'parlistlabel'])
                          .length === 0
                      }
                    />
                  )}
                </Form.Item>
              </Col>
              <Col>
                <AntButton
                  danger
                  htmlType="button"
                  icon={
                    <CloseOutlined height="14px" width="14px" color="white" />
                  }
                  onClick={() => {
                    setValueByNamePath(
                      form,
                      getName('parvalue')(record, indexById[record.id]),
                      ''
                    );
                    remove(record.parvalue);
                  }}
                />
              </Col>
            </Row>
          ) : (
            <Row>
              {/*<Col>*/}
              {/*  <TasksListPopover*/}
              {/*    entityId={record.plistid}*/}
              {/*    entityName="PARLIST"*/}
              {/*    title={record.parlistlabel}*/}
              {/*    entityReference={record.parvalue}*/}
              {/*    parcode={form.getFieldValue('parcode')}*/}
              {/*    parvalue={record.parvalue}*/}
              {/*    parvalueparent={record.parvalueparent}*/}
              {/*    onClose={refetch}*/}
              {/*  >*/}
              {/*    /!*<AntdButton*!/*/}
              {/*    /!*  size="middle"*!/*/}
              {/*    /!*  icon={<IActions height="15px" width="15px" />}*!/*/}
              {/*    /!*>*!/*/}
              {/*    /!*  <GetLabel label="actions" />*!/*/}
              {/*    /!*</AntdButton>*!/*/}
              {/*  </TasksListPopover>*/}
              {/*</Col>*/}

              <Col>
                <Popconfirm
                  disabled={record?.isNew}
                  title="Are you sure to edit this value?"
                  onConfirm={() => onSaveOrEdit(record)}
                  okText="Yes"
                  cancelText="No"
                >
                  <SaveButtonIcon size="middle" onlyIcon />
                </Popconfirm>
              </Col>
              <Col>
                {userrole === 'ADMIN' && (
                  <Popconfirm
                    title="Are you sure to delete this value?"
                    onConfirm={() => {
                      const parcode = form.getFieldValue('parcode') ?? '';
                      if (!('isNew' in record)) {
                        removeParList({
                          parcode,
                          parvalue: record.parvalue,
                          req: { ...record, parcode },
                        }).then((res) => {
                          if (res.code === '200')
                            notification.success({
                              message: ` ${record.parvalue} is  successfully deleted`,
                            });

                          refetch();
                        });
                      }
                      remove(record.parvalue);
                    }}
                    okText="Yes"
                    cancelText="No"
                  >
                    <DeleteButton onlyIcon />
                  </Popconfirm>
                )}
              </Col>
            </Row>
          ),
        width: 200,
        fixed: 'right',
      },
    ],
    [getName, ParcodeParent, form, indexById, remove, refetch, onSaveOrEdit]
  );

  const addRow = () => {
    const row: any = {
      parlistdescription: '',
      parlistlabel: '',
      parvalue: '',
      parvalueparent: '',
      parvalueparentlabel: null,
      isNew: true,
      id: -1,
      parliststatus: '',
      parliststatuslabel: '',
      parvalueexternal:''
    };
    add(row);
  };

  const filteredData = useMemo(() => {
    const { parvalue, parlistlabel, parvalueparent } = searchParams;
    return data.filter((value: any) => {
      if (parvalue === '' && parlistlabel === '' && parvalueparent === '') {
        return true;
      }
      return (
        (parvalue === '' || value?.parvalue?.includes(parvalue)) &&
        (parlistlabel === '' || value?.parlistlabel?.includes(parlistlabel)) &&
        (parvalueparent === '' || value?.parvalueparent?.includes(parvalueparent))
      );
    });
  }, [searchParams, data]);

  return (
    <DataGrid
      rowKey="id"
      titleComcode="listOfValue"
      dataSource={filteredData}
      columns={columns}
      onSearch={(values) => {
        setSearchParams(values);
        setsearchs(true);
      }}
      initialSearchValues={initialSearchParams}
      extractData='COMPLETNES'
      extraActions={
        <Button
          comcode="Add"
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => !isInspect && addRow()}
          disabled={some(form.getFieldValue(fieldName), 'isNew')}
        />
      }
    />
  );
};

export default ParameterValuesTable;
